import React, { useState, useEffect, useCallback } from 'react';
import { Eye, EyeOff, Check, X } from 'lucide-react';

const PasswordStrength = ({ 
  value, 
  onChange, 
  onValidationChange, 
  placeholder = "הזן סיסמה",
  showToggle = true,
  className = "input-field",
  error = null,
  id,
  ...props 
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [strength, setStrength] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [errors, setErrors] = useState([]);

  // Client-side validation
  const validatePassword = (password) => {
    const errors = [];
    
    if (password.length < 10) {
      errors.push('הסיסמה חייבת להכיל לפחות 10 תווים');
    }
    
    if (password.length > 20) {
      errors.push('הסיסמה לא יכולה להכיל יותר מ-20 תווים');
    }
    
    if (!/[A-Z]/.test(password)) {
      errors.push('הסיסמה חייבת להכיל לפחות אות גדולה אחת');
    }
    
    if (!/[a-z]/.test(password)) {
      errors.push('הסיסמה חייבת להכיל לפחות אות קטנה אחת');
    }
    
    if (!/\d/.test(password)) {
      errors.push('הסיסמה חייבת להכיל לפחות מספר אחד');
    }
    
    if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~`]/.test(password)) {
      errors.push('הסיסמה חייבת להכיל לפחות תו מיוחד אחד');
    }
    
    // Check for consecutive identical characters
    if (/(.)\1{2,}/.test(password)) {
      errors.push('הסיסמה לא יכולה להכיל יותר מ-2 תווים זהים ברצף');
    }
    
    // Check for sequential numbers
    if (/123|234|345|456|567|678|789|890/.test(password)) {
      errors.push('הסיסמה לא יכולה להכיל מספרים עוקבים');
    }
    
    // Check for sequential letters
    if (/abc|bcd|cde|def|efg|fgh|ghi|hij|ijk|jkl|klm|lmn|mno|nop|opq|pqr|qrs|rst|stu|tuv|uvw|vwx|wxy|xyz/i.test(password)) {
      errors.push('הסיסמה לא יכולה להכיל אותיות עוקבות');
    }
    
    // Check for keyboard patterns
    if (/qwerty|asdf|zxcv/i.test(password)) {
      errors.push('הסיסמה לא יכולה להכיל דפוסי מקלדת');
    }
    
    // Check for common passwords
    if (/password|123456|admin|user/i.test(password)) {
      errors.push('הסיסמה מכילה דפוסים נפוצים וחלשים');
    }
    
    return {
      isValid: errors.length === 0,
      errors: errors,
      strength: calculateStrength(password)
    };
  };

  const calculateStrength = (password) => {
    let score = 0;
    
    // Length score (0-40 points)
    if (password.length >= 10) score += 15;
    if (password.length >= 15) score += 15;
    if (password.length >= 20) score += 10;
    
    // Character variety (0-30 points)
    if (/[a-z]/.test(password)) score += 5;
    if (/[A-Z]/.test(password)) score += 5;
    if (/\d/.test(password)) score += 5;
    if (/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~`]/.test(password)) score += 10;
    if (/[^a-zA-Z0-9!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~`]/.test(password)) score += 5;
    
    // Complexity (0-30 points)
    const uniqueChars = new Set(password).size;
    if (uniqueChars >= password.length * 0.6) score += 15;
    if (uniqueChars >= password.length * 0.8) score += 15;
    
    return Math.min(100, score);
  };

  const getStrengthColor = (strength) => {
    if (strength < 30) return 'bg-red-500';
    if (strength < 50) return 'bg-orange-500';
    if (strength < 70) return 'bg-yellow-500';
    if (strength < 90) return 'bg-blue-500';
    return 'bg-green-500';
  };

  const getStrengthText = (strength) => {
    if (strength < 30) return 'חלשה מאוד';
    if (strength < 50) return 'חלשה';
    if (strength < 70) return 'בינונית';
    if (strength < 90) return 'טובה';
    return 'חזקה';
  };

  const getStrengthTextColor = (strength) => {
    if (strength < 30) return 'text-red-600';
    if (strength < 50) return 'text-orange-600';
    if (strength < 70) return 'text-yellow-600';
    if (strength < 90) return 'text-blue-600';
    return 'text-green-600';
  };

  const validatePasswordCallback = useCallback(validatePassword, []);

  useEffect(() => {
    if (value) {
      const validation = validatePasswordCallback(value);
      setStrength(validation.strength);
      setErrors(validation.errors);
      setFeedback(getStrengthText(validation.strength));
      
      if (onValidationChange) {
        onValidationChange(validation);
      }
    } else {
      setStrength(0);
      setErrors([]);
      setFeedback('');
      
      if (onValidationChange) {
        onValidationChange({ isValid: false, errors: [], strength: 0 });
      }
    }
  }, [value, onValidationChange, validatePasswordCallback]);

  const handleChange = (e) => {
    onChange(e);
  };

  return (
    <div className="space-y-3">
      <div className="relative">
        <input
          id={id}
          type={showPassword ? 'text' : 'password'}
          value={value}
          onChange={handleChange}
          placeholder={placeholder}
          className={`${className} ${error ? 'border-red-500' : ''} pr-12`}
          {...props}
        />
        {showToggle && (
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-brown-500 hover:text-brown-700"
            aria-label={showPassword ? 'הסתר סיסמה' : 'הצג סיסמה'}
          >
            {showPassword ? <EyeOff className="h-5 w-5" aria-hidden="true" /> : <Eye className="h-5 w-5" aria-hidden="true" />}
          </button>
        )}
      </div>

      {/* Password Strength Indicator */}
      {value && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-brown-700">עוצמת הסיסמה:</span>
            <span className={`text-sm font-semibold ${getStrengthTextColor(strength)}`}>
              {feedback}
            </span>
          </div>
          
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all duration-300 ${getStrengthColor(strength)}`}
              style={{ width: `${strength}%` }}
            />
          </div>
        </div>
      )}

      {/* Password Requirements */}
      {value && (
        <div className="space-y-1">
          <div className="text-sm font-medium text-brown-700 mb-2">דרישות סיסמה:</div>
          <div className="grid grid-cols-1 gap-1 text-sm">
            <div className={`flex items-center ${value.length >= 10 && value.length <= 20 ? 'text-green-600' : 'text-red-600'}`}>
              {value.length >= 10 && value.length <= 20 ? <Check className="h-4 w-4 ml-2" /> : <X className="h-4 w-4 ml-2" />}
              בין 10 ל-20 תווים
            </div>
            <div className={`flex items-center ${/[A-Z]/.test(value) ? 'text-green-600' : 'text-red-600'}`}>
              {/[A-Z]/.test(value) ? <Check className="h-4 w-4 ml-2" /> : <X className="h-4 w-4 ml-2" />}
              אות גדולה אחת לפחות
            </div>
            <div className={`flex items-center ${/[a-z]/.test(value) ? 'text-green-600' : 'text-red-600'}`}>
              {/[a-z]/.test(value) ? <Check className="h-4 w-4 ml-2" /> : <X className="h-4 w-4 ml-2" />}
              אות קטנה אחת לפחות
            </div>
            <div className={`flex items-center ${/\d/.test(value) ? 'text-green-600' : 'text-red-600'}`}>
              {/\d/.test(value) ? <Check className="h-4 w-4 ml-2" /> : <X className="h-4 w-4 ml-2" />}
              מספר אחד לפחות
            </div>
            <div className={`flex items-center ${/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~`]/.test(value) ? 'text-green-600' : 'text-red-600'}`}>
              {/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~`]/.test(value) ? <Check className="h-4 w-4 ml-2" /> : <X className="h-4 w-4 ml-2" />}
              תו מיוחד אחד לפחות
            </div>
          </div>
        </div>
      )}

      {/* Error Messages */}
      {error && (
        <p className="text-red-600 text-sm">{error}</p>
      )}
      
      {errors.length > 0 && (
        <div className="space-y-1">
          {errors.map((error, index) => (
            <p key={index} className="text-red-600 text-sm flex items-center">
              <X className="h-4 w-4 ml-2" />
              {error}
            </p>
          ))}
        </div>
      )}
    </div>
  );
};

export default PasswordStrength;
